// Angular
import { Component, inject, signal } from '@angular/core';
import {
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
// PrimeNG
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { SidebarModule } from 'primeng/sidebar';
import { PasswordModule } from 'primeng/password';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
// Directives
import { ControlErrorsDirective } from '@shared/directives-v3/validation-message/directives/control-errors/control-errors.directive';
import { FormSubmitDirective } from '@shared/directives-v3/validation-message/directives/form-submit/form-submit.directive';
// Components
import { EmailInputComponent } from '@shared/components-v3/atomic/email-input/email-input.component';
import { SessionStorageService } from '@core/storage/session-storage.service';
import { AuthApiService } from '@core/services-v2/auth/auth.service';
import { WishlistService } from '@core/services-v2/wishlist/wishlist.service';
import { ILoginResponse } from '@core/services-v2/auth/models/login-response.interface';
import { ISession } from '@core/models-v2/auth/session.interface';
import { SessionTokenStorageService } from '@core/storage/session-token-storage.service';
import { GuestStorageService } from '@core/storage/guest-storage.service';
import { AuthStateServiceV2 } from '@core/services-v2/session/auth-state.service';
import { CartService } from '@core/services-v2/cart.service';
import { SessionService } from '@core/services-v2/session/session.service';
import { CartApiService } from '@core/services-v2/cart/cart-api/cart-api.service';
import { CheckoutStorageService } from 'src/app/checkout-v2/services/checkout-storage/checkout-storage.service';

const NG_MODULES = [ReactiveFormsModule];
const PRIME_MODULES = [
  SidebarModule,
  ButtonModule,
  DividerModule,
  PasswordModule,
  ToastModule,
];
const COMPONENTS = [EmailInputComponent];
const DIRECTIVES = [FormSubmitDirective, ControlErrorsDirective];

@Component({
  selector: 'app-login-sidebar',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, COMPONENTS, DIRECTIVES],
  templateUrl: './login-sidebar.component.html',
  styleUrl: './login-sidebar.component.scss',
  providers: [MessageService],
})
export class LoginSidebarComponent {
  visible: boolean = false;
  isLoading = signal<boolean>(false);
  loginForm!: FormGroup;

  private readonly fb = inject(NonNullableFormBuilder);
  private readonly router = inject(Router);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly sessionTokenStorage = inject(SessionTokenStorageService);
  private readonly authStateService = inject(AuthStateServiceV2);
  private readonly guestStorage = inject(GuestStorageService);
  private readonly authService = inject(AuthApiService);
  private readonly wishListService = inject(WishlistService);
  private readonly cartService = inject(CartService);
  private readonly cartApiService = inject(CartApiService);
  private readonly sessionService = inject(SessionService);
  private readonly checkoutStorageService = inject(CheckoutStorageService);
  private readonly messageService = inject(MessageService);

  constructor() {
    this.buildForm();
    this.mySession();
  }

  private buildForm(): void {
    this.loginForm = this.fb.group({
      email: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  private mySession(): void {
    const session = this.sessionStorage.get();
    if (session) {
      this.visible = false;
    }
  }

  goToRegister() {
    this.router.navigate(['/register']);
  }

  show(): void {
    this.visible = true;
  }

  goToCheckout(): void {
    //this.router.navigate(['/checkout']);
    this.router.navigate(['/carro-compra/metodo-de-envio']);
  }

  goToCreateAccount(): void {
    this.router.navigate(['/auth/register']);
  }

  login(): void {
    if (this.loginForm.valid) {
      this.isLoading.set(true);
      const { email, password } = this.loginForm.getRawValue();
      this.authService.login(email, password).subscribe({
        next: (res) => {
          const session = this.sessionStorage.get();
          let previousUserId: string | null = session?.email || null;
          this.setSession(res, previousUserId);
          if (this.sessionService.isB2B()) {
            this.router
              .navigate(['/carro-compra/metodo-de-envio'])
              .then(() => {
                window.location.reload();
              });
          } else {
            this.goToCheckout();
          }
        },
        error: (err) => {
          console.error(err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Credenciales inválidas',
          });
          this.isLoading.set(false);
        },
      });
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  private setSession(
    value: ILoginResponse,
    previousUserId: string | null
  ): void {
    const iva = value.user.preferences.iva ?? true;
    const session: ISession = {
      ...value.user,
      login_temp: false,
      preferences: { iva },
    };
    this.sessionStorage.set(session);
    this.sessionTokenStorage.set(value.token);
    this.guestStorage.remove();
    this.authStateService.setSession(session);
    this.setShoppingCart(previousUserId, session.email);
    this.setWishList(value.user.documentId);
    //nuevo
    this.checkoutStorageService.removeAll();
  }

  private setShoppingCart(
    previousUserId: string | null,
    currentUserId: string
  ): void {
    if (previousUserId) {
      this.cartApiService
        .transfer({
          origin: previousUserId,
          destination: currentUserId,
        })
        .subscribe(() => {
          this.cartService.load();
        });
    } else {
      this.cartService.load();
    }
  }

  private setWishList(documentId: string): void {
    this.wishListService.setWishlistsOnStorage(documentId).subscribe();
  }
}
